import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Loader2, CheckCircle } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
  useCreateOrderMutation,
  useEnrollCourseMutation,
  useGetMyOrdersQuery,
} from "@/features/api/paymentApi";

import { useGetCourseBySlugQuery } from "@/features/api/courseApi";
import { useGetMockTestByIdQuery } from "@/features/api/mockTestApi";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth || {});

  /* ---------------- GET TYPE + ID ---------------- */
  const { type, id } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      type: params.get("type"),
      id: params.get("id"),
    };
  }, [location.search]);

  /* ---------------- FETCH PRODUCT ---------------- */
  const { data: courseData } =
    useGetCourseBySlugQuery(id, { skip: type !== "COURSE" });

  const { data: mockData } =
    useGetMockTestByIdQuery(id, { skip: type !== "MOCK_TEST" });

  const course = courseData?.course;
  const mock = mockData?.mock;

  const product = useMemo(() => {
    if (type === "COURSE" && course) {
      return {
        id: course._id,
        name: course.title,
        price: Number(course.price) || 0,
        image: course.featuredImage,
        type: "COURSE",
      };
    }

    if (type === "MOCK_TEST" && mock) {
      return {
        id: mock._id,
        name: mock.name,
        price: Number(mock.price) || 0,
        image: mock.image,
        type: "MOCK_TEST",
      };
    }

    return null;
  }, [type, course, mock]);

  /* ---------------- FETCH USER ORDERS ---------------- */
  const { data: ordersData } = useGetMyOrdersQuery(undefined, {
    skip: !isAuthenticated,
  });

  const myOrders = ordersData?.data || [];

  /* ---------------- CHECK IF ALREADY PURCHASED ---------------- */
  const isAlreadyPurchased = useMemo(() => {
    if (!product || !isAuthenticated) return false;

    return myOrders.some(
      (order) =>
        order.courseId === product.id && order.status === "SUCCESS"
    );
  }, [product, myOrders, isAuthenticated]);

  /* ---------------- FORM ---------------- */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: String(user?.phone || ""),
      });
    }
  }, [isAuthenticated, user]);

  /* ---------------- TAX ---------------- */
  const price = product?.price || 0;
  const cgst = Number((price * 0.09).toFixed(2));
  const sgst = Number((price * 0.09).toFixed(2));
  const totalAmount = Number((price + cgst + sgst).toFixed(2));

  /* ---------------- API ---------------- */
  const [createOrder, { isLoading }] = useCreateOrderMutation();
  const [enrollCourse, { isLoading: isEnrollLoading }] =
    useEnrollCourseMutation();

  /* ---------------- PAYMENT HANDLER ---------------- */
  const handlePayment = async (e) => {
    e.preventDefault();

    if (!product) return;

    if (isAlreadyPurchased) {
      toast.info("You already purchased this item.");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept Terms & Conditions");
      return;
    }

    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.replace(/\D/g, "");

    if (!name || !email || !phone) {
      toast.error("All fields are required");
      return;
    }

    try {
      if (totalAmount === 0 && product.type === "COURSE") {
        const res = await enrollCourse({ courseId: product.id }).unwrap();

        if (res?.success) {
          toast.success("Enrolled Successfully");
          navigate(`/courses/${product.id}/learn`);
        }

        return;
      }

      const res = await createOrder({
        amount: totalAmount,
        phone,
        courseId: product.id,
        courseName: product.name,
        name,
        email,
        productType: product.type,
      }).unwrap();

      if (!res?.success || !res?.token) {
        toast.error("Payment initialization failed");
        return;
      }

      window.PhonePeCheckout.init({
        token: res.token,
        callback: function (response) {
          if (response?.state === "COMPLETED") {
            navigate(`/success?orderId=${res.transactionId}`);
          } else {
            navigate(`/failed?orderId=${res.transactionId}`);
          }
        },
      });

    } catch (err) {
      toast.error(err?.data?.message || "Payment failed");
    }
  };

  if (!product) return null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <form onSubmit={handlePayment} className="grid lg:grid-cols-3 gap-8">
        {/* BILLING */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Billing Details</CardTitle>
              <CardDescription>
                Enter your details to complete purchase
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Full Name"
              />

              <Input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="Email"
              />

              <Input
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="Phone"
              />

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <Label>I agree to Terms & Conditions</Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SUMMARY */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <h2 className="font-semibold text-lg">{product.name}</h2>

              {/* 🔥 PURCHASED BADGE */}
              {isAlreadyPurchased && (
                <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm font-medium">
                  <CheckCircle size={16} />
                  Already Purchased
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Price</span>
                  <span>₹{price.toFixed(2)}</span>
                </div>

                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* 🔥 BUTTON LOGIC */}
              {isAlreadyPurchased ? (
                <Button
                  type="button"
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() =>
                    product.type === "COURSE"
                      ? navigate(`/courses/${product.id}/learn`)
                      : navigate(`/mock-tests/${product.id}`)
                  }
                >
                  Go to {product.type === "COURSE" ? "Course" : "Mock Test"}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={
                    isLoading ||
                    isEnrollLoading ||
                    !termsAccepted
                  }
                  className="w-full"
                >
                  {(isLoading || isEnrollLoading) ? (
                    <Loader2 className="animate-spin" />
                  ) : totalAmount === 0 ? (
                    "Enroll Now"
                  ) : (
                    "Proceed to Pay"
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}